import React from 'react';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { Toaster } from 'react-hot-toast';
import { AppThemeProvider } from './hooks/ThemeContext';
import { AuthProvider, useAuth } from './hooks/AuthContext';
import { TenderProvider } from './hooks/TenderContext';
import Layout from './components/Layout/Layout';
import DashboardPage from './pages/DashboardPage';
import { loginRequest } from './services/authConfig';
import { useMsal } from '@azure/msal-react';
import { msalInstance, initializeMsal } from './services/authConfig';
import { Box, CircularProgress, Typography, Button } from '@mui/material';

// Auto redirect to Microsoft login
const AutoLogin = () => {
  const { instance } = useMsal();
  React.useEffect(() => {
    instance.loginRedirect(loginRequest);
  }, [instance]);
  return <LoadingScreen />;
};

// Loading spinner
const LoadingScreen = () => (
  <Box sx={{
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', gap: 2,
  }}>
    <CircularProgress size={48} />
    <Typography color="text.secondary">טוען מערכת...</Typography>
  </Box>
);

// Access denied screen
const AccessDenied = () => {
  const { authError, logout } = useAuth();
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', gap: 2, p: 3, textAlign: 'center',
    }}>
      <Typography variant="h4" fontWeight={700} color="error">גישה נדחתה</Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 400 }}>
        {authError || 'אין לך הרשאה לגשת למערכת. פנה למנהל המערכת לקבלת גישה.'}
      </Typography>
      <Button variant="outlined" onClick={logout} sx={{ mt: 2 }}>התנתק</Button>
    </Box>
  );
};

// Main authenticated app
const AuthenticatedApp = () => {
  const { loading, authError, currentUser } = useAuth();

  if (loading) return <LoadingScreen />;
  if (authError || !currentUser) return <AccessDenied />;

  return (
    <TenderProvider>
      <Layout>
        <DashboardPage />
      </Layout>
    </TenderProvider>
  );
};

function App() {
  const [msalReady, setMsalReady] = React.useState(false);

  React.useEffect(() => {
    initializeMsal().then(() => setMsalReady(true));
  }, []);

  if (!msalReady) return <LoadingScreen />;

  return (
    <MsalProvider instance={msalInstance}>
      <AppThemeProvider>
        <AuthenticatedTemplate>
          <AuthProvider>
            <AuthenticatedApp />
          </AuthProvider>
        </AuthenticatedTemplate>

        <UnauthenticatedTemplate>
          <AutoLogin />
        </UnauthenticatedTemplate>

        <Toaster
          position="bottom-left"
          toastOptions={{
            duration: 3000,
            style: {
              direction: 'rtl',
              fontFamily: 'Rubik, sans-serif',
              borderRadius: 10,
            },
          }}
        />
      </AppThemeProvider>
    </MsalProvider>
  );
}

export default App;
